import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Micro from '@amcharts/amcharts5/themes/Micro';
import * as am5 from '@amcharts/amcharts5';
import { DateTime } from 'luxon';
import { ElementRef } from '@angular/core';
import { formatCurrency } from '@angular/common';

export const getCurrencyLabel = (currency: number) => {
    return formatCurrency(currency, 'en-US', '$', 'USD');
};

export function createGraph(
    rates: { date?: Date; rate?: number }[],
    container: ElementRef<any>,
    vehicleType: { fill: am5.Color; fillDark: am5.Color },
) {
    const data: { month: string; rate: number; rateLabel: string }[] =
        rates.map((rate) => {
            const dateTime = DateTime.fromJSDate(rate.date);
            return {
                month: dateTime.toFormat('LLL').toUpperCase(),
                rate: rate.rate,
                rateLabel: getCurrencyLabel(rate.rate),
            };
        });
    const root = am5.Root.new(container.nativeElement);

    root.setThemes([am5themes_Animated.new(root), am5themes_Micro.new(root)]);

    let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            paddingLeft: 0,
            paddingRight: 1,
        }),
    );

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    let xRenderer = am5xy.AxisRendererX.new(root, {
        minGridDistance: 30,
    });

    xRenderer.grid.template.setAll({
        location: 1,
    });

    let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
            categoryField: 'month',
            renderer: xRenderer,
        }),
    );
    let yRenderer = am5xy.AxisRendererY.new(root, {
        strokeOpacity: 0,
    });
    let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: yRenderer,
            panX: false,
            panY: false,
            min: 0,
            extraMax: 0.04,
        }),
    );
    let series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
            name: 'Flatbed Rates',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'rate',
            sequencedInterpolation: true,
            categoryXField: 'month',
        }),
    );

    series.columns.template.setAll({
        stroke: vehicleType.fill,
        fill: vehicleType.fill,
        width: 30,
    });

    series.bullets.push(function () {
        return am5.Bullet.new(root, {
            locationY: 1,
            locationX: 0,
            sprite: am5.Label.new(root, {
                text: '{month}',
                fill: am5.color(0xffffff),
                width: 30,
                background: am5.Rectangle.new(root, {
                    fill: vehicleType.fillDark,
                    width: 35,
                    height: 16,
                }),
                textAlign: 'center',
                fontSize: 10,
                fontWeight: '900',
                populateText: true,
            }),
        });
    });

    series.bullets.push(function () {
        return am5.Bullet.new(root, {
            locationY: 1.1,
            locationX: -0.33,
            sprite: am5.Label.new(root, {
                fontWeight: '900',
                fontSize: 10,
                text: '{rateLabel}',
                fill: am5.color(0xffffff),
                populateText: true,
            }),
        } as am5.IBulletSettings);
    });

    xAxis.data.setAll(data);
    series.data.setAll(data);

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    series.appear(1000).then(() => {});
    chart.appear(1).then(() => {
        series.columns.getIndex(3).setAll({
            fillPattern: am5.LinePattern.new(root, {
                color: am5.color('#192129'),
                rotation: 315,
                width: 200,
                height: 200,
            }),
        });
    });
}
